import "../i18n";
import { Sentry } from "../sentryInstrumentReact";
import React, { useEffect, useState } from 'react';
import Cookies from 'js-cookie';

import ShoppingCartRow from './ShoppingCartRow';
import { updateCartIcons } from './updateCartIcons';
import CheckoutDiscount from './CheckoutDiscount';
import { useCart } from './CartContext';
import ProcessingSpinner from '../components/ProcessingSpinner';
import NoItems from './NoItems';
import StoreHeader from './StoreHeader';

function ShoppingCart() {
  const {
    setCartSubtotal,
    setDiscountAmount,
    setDiscountLocked,
    setDiscountCode,
    setCartSubtotalWithDiscount,
    rendering,
    setRendering,
   } = useCart();
  const [totalItems, setTotalItems] = useState(0);
  const [cartItems, setCartItems] = useState([]);
  const [isCheckoutDisabled, setIsCheckoutDisabled] = useState(false);
  const csrftoken = Cookies.get('csrftoken');


  // fetch cart contents on load
  useEffect(() => {
    async function fetchCartData() {
	  try {
      const response = await fetch("/store/_api/cart/");
      const data = await response.json();
      setCartItems(data.items);
      setCartSubtotal(parseFloat(data.subtotal).toFixed(2));
      setRendering(false);
	  } catch(error) {
		  Sentry.captureException?.(error, {stage: "fetch cart data"});
	  }
    }
    fetchCartData();
  }, []);


  // update cart icon in header cart changes
  useEffect(() => {
    if (
      cartItems.some((cartItem) => isNaN(cartItem.quantity)) ||
      cartItems.some((cartItem) => parseInt(cartItem.quantity) <= 0)
    ) {
      console.log("don't update cart because input field is empty string or <= 0");
    } else {
      let itemsCount = 0;
      let subTotalCount = 0;
      cartItems.map((item) => {
        itemsCount += parseInt(item.quantity);
        subTotalCount += parseFloat(item.subtotal);
      });
      setTotalItems(itemsCount);
      setCartSubtotal(subTotalCount.toFixed(2));
      updateCartIcons(itemsCount);
    }

  }, [cartItems]);


  async function deleteQuantity(id) {

    // sets animation before deletion
    setCartItems((prevCartItems) =>
      prevCartItems.map((item) =>
        id === item.id ? {...item, itemClass: "cart-row fade-element"} : item
      )
    );

    setTimeout(async () => {
      const newCart = cartItems.filter(item => item.id !== id);
      setCartItems(newCart);
      try {
        const response = await fetch(`/store/_api/cart/${id}/`, {
          method: "DELETE",
          headers: {"Content-Type": "application/json", "X-CSRFToken": csrftoken},
        });
        const data = await response.json();
        updateCartIcons(data.count);

        if (data.discount_amount === 0) {
          setDiscountCode('');
          setDiscountAmount(0);
          setDiscountLocked(false);
        } else {
          setDiscountAmount(parseFloat(data.discount_amount).toFixed(2));
        }
        setTotalItems(parseInt(data.count));
        setCartSubtotal(parseFloat(data.subtotal).toFixed(2));
        setCartSubtotalWithDiscount(parseInt(data.discounted_subtotal).toFixed(2));


      } catch (error) {
        Sentry.captureException(error);
      }
    }, 1000);

  }


  // Throttling state for quantity changes
  const pendingQuantityUpdates = React.useRef({current: {}});

  async function processQuantityUpdate(productId, newQty) {
    try {
      const response = await fetch(`/store/_api/cart/${productId}/quantity/`, {
			method: "PUT",
			headers: {"Content-Type": "application/json", "X-CSRFToken": csrftoken},
			body: JSON.stringify({quantity: newQty}),
		});

      if(!response.ok) {
        throw new Error(`API update failed to update a product id: ${productId}`);
      }

      const data = await response.json();
      if (data.quantity) {
        const error = new Error("Quantity exceeded");
        error.data = data;
        throw error;
      }

      setCartItems(data.items);

      // handle all other cart data
      if (data.discount_amount === 0) {
        setDiscountCode('')
        setDiscountAmount(0);
      } else {
        setDiscountAmount(parseFloat(data.discount_amount).toFixed(2));
      }
      setTotalItems(parseInt(data.count));
      setCartSubtotal(parseFloat(data.subtotal).toFixed(2));
      setCartSubtotalWithDiscount(parseInt(data.discounted_subtotal).toFixed(2));

    } catch (error) {
      // update cart with errors if api update fails
      Sentry.captureException(error);
    }
  }

  function quantityChange(productId, newQuantity, minQuantity, maxQuantity) {
	minQuantity = minQuantity || 1;
	maxQuantity = maxQuantity || Number.MAX_SAFE_INTEGER;
    const newQty = newQuantity < minQuantity ? minQuantity : newQuantity;

    // Update UI immediately for responsive button clicks
    const optimisticNewCart = cartItems.map((item) => {
      if (item.id === productId) {
        return {
          ...item,
          quantity: newQty,
          subtotal: newQty * parseFloat(item.price).toFixed(2),
        }
      }
      return item;
    });
    setCartItems(optimisticNewCart);

    // Get current state before any pending changes
    const currentItem = cartItems.find(item => item.id === productId);
    if (!pendingQuantityUpdates.current[productId]) {
      // Store original quantity at start of throttle window
      pendingQuantityUpdates.current[productId] = {
        originalQuantity: currentItem.quantity,
        timer: null,
        targetChange: 0
      };
    }

    // Clear any existing timer
    if (pendingQuantityUpdates.current[productId].timer) {
      clearTimeout(pendingQuantityUpdates.current[productId].timer);
    }

    // Calculate change from original quantity
    const targetChange = newQty - pendingQuantityUpdates.current[productId].originalQuantity;

    // Update pending changes
    const currentTargetChange = pendingQuantityUpdates.current[productId].targetChange;
    pendingQuantityUpdates.current[productId] = {
      ...pendingQuantityUpdates.current[productId],
      targetChange: targetChange > 0 ? Math.max(currentTargetChange ?? 0, targetChange) : Math.min(currentTargetChange ?? 0, targetChange),
      timer: setTimeout(() => {
        const pending = pendingQuantityUpdates.current[productId];
        if (pending) {
          // Calculate final quantity based on original quantity plus accumulated change
          const finalQuantity = pending.originalQuantity + pending.targetChange;
          processQuantityUpdate(productId, Math.max(minQuantity, finalQuantity));
          delete pendingQuantityUpdates.current[productId];
        }
      }, 800)
    };
  }


  function handleQuantityInputChange(productId, newQty, min, max) {

    newQty = parseInt(newQty);
    if (newQty >= min && (newQty <= max || max === null) ) {

      const newCart = cartItems.map((cartItem) => {
        if (cartItem.productDetails.id === productId) {
          return {
            ...cartItem,
            quantity: newQty,
            subtotal: newQty * cartItem.price,
            error: '',
          }
        } else {
          return cartItem;
        }
      });
      setIsCheckoutDisabled(false);
      setCartItems(newCart);

    } else {
      const newCart = cartItems.map((cartItem) => {
        if (cartItem.productDetails.id === productId) {
          return {
            ...cartItem,
            quantity: newQty,
            error: window.gettext("Quantity must be between ") + min + " " + window.gettext("and") + " " + max,
          };
        }
        return cartItem;
      });
      setIsCheckoutDisabled(true);
      setCartItems(newCart);

    }

  }


  if (rendering) {
    return(
      <>
        <ProcessingSpinner style={{margin: "5rem auto 4rem",}} />
      </>
    );
  }


  if (totalItems === 0) {
    return(
      <>
        <StoreHeader
          h1Text={window.gettext("Cart")}
          totalItems={totalItems}
          headerType="cart"
        />
        <NoItems />
      </>
    );
  }


  return (
    <>
      <StoreHeader
        h1Text={window.gettext("Cart")}
        totalItems={totalItems}
        headerType="cart"
      />
      <div className="cart-body">
        <div className="container">
          <div className="cart-rows">

              { cartItems.map((item) => (
                <ShoppingCartRow
                  itemClass={item.itemClass}
                  key={item.id}
                  id={item.id}
                  quantity={item.quantity}
                  productDetails={item.productDetails}
                  variantDetails={item.variantDetails}
                  handleQuantityInputChange={(e) => {
                    const newQty = parseInt(Math.abs(e.target.value));
                    handleQuantityInputChange(item.productDetails.id, newQty, item.variantDetails.minimum_quantity, item.variantDetails.maximum_quantity);
                  }}
                  handleOnBlur={(e) => {
                    if (!isCheckoutDisabled) {
                      const newQty = parseInt(Math.abs(e.target.value));
                      quantityChange(item.id, newQty, item.variantDetails.minimum_quantity, item.variantDetails.maximum_quantity);
                    }
                  }}
                  handleOnKeyUp={(e) => {
                    if (e.key === "Enter") {
                      const newQty = parseInt(Math.abs(e.target.value));
                      quantityChange(item.id, newQty, item.variantDetails.minimum_quantity, item.variantDetails.maximum_quantity);
                    }
                  }}
                  handleQuantityDecrease={() => quantityChange(
                    item.id,
                    item.quantity - 1,
                    item.productDetails.minimum_quantity,
                    item.productDetails.maximum_quantity,
                  )}
                  handleQuantityIncrease={() => quantityChange(
                    item.id,
                    item.quantity + 1,
                    item.productDetails.minimum_quantity,
                    item.productDetails.maximum_quantity,
                  )}
                  handleRemoveProduct={() => deleteQuantity(item.id)}
                  error={item.error}
                />
              ))}

          </div>
          <div className="cart-footer">
              <CheckoutDiscount />

                <div className="ctas">
                  <a href={window.gettext("/store/")} className="btn btn-cart-secondary">{window.gettext("Continue Shopping")}</a>
                  {isCheckoutDisabled === true ?
                    <div className="btn disabled">{window.gettext("Checkout Disabled")}</div> :
                    <a href={window.gettext("/store/checkout/")} className="btn btn-primary btn-spotlight">{window.gettext("Proceed to Checkout")}</a>
                  }
                </div>
              </div>
        </div>
      </div>
      {window.SITE_ACRONYM !== "ANCP" && (
        <div className="container narrow">
          <p
            className="church-msg balance text-center"
            dangerouslySetInnerHTML={{
              __html: window.gettext('<strong>Purchasing on behalf of a church or tax-exempt organization?</strong><br />Please contact Ministry Services at <a href="tel:1-800-569-5959" title="Call Revive Our Hearts">1-800-569-5959</a> before placing your order.')
            }}
          ></p>
        </div>
      )}
    </>
  );
}

export default ShoppingCart;
